import { API_BASE_URL } from '../config/api';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('jwt_token');
    this.user = null;
    this.loadUserFromToken();
  }

  // Decode JWT token to get user info
  decodeToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  // Load user data from stored token
  loadUserFromToken() {
    if (this.token) {
      const decoded = this.decodeToken(this.token);
      if (decoded && decoded.exp > Date.now() / 1000) {
        this.user = decoded;
      } else {
        // Token expired
        this.logout();
      }
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.token && this.user && this.user.exp > Date.now() / 1000;
  }

  // Get current user
  getCurrentUser() {
    return this.user;
  }

  // Login with JWT
  async login(username, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/login_jwt.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      if (data.success && data.token) {
        // Store token and user data
        this.token = data.token;
        localStorage.setItem('jwt_token', this.token);
        
        // Decode token to get user info
        this.user = this.decodeToken(this.token);
        
        return {
          success: true,
          user: data.user,
          token: data.token
        };
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (error) {
      throw error;
    }
  }

  // Logout
  logout() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('jwt_token');
  }

  // Get auth headers for API requests
  getAuthHeaders() {
    if (this.token) {
      return {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json'
      };
    }
    return {
      'Content-Type': 'application/json'
    };
  }

  // Make authenticated API request
  async authenticatedFetch(url, options = {}) {
    if (!this.isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    const authOptions = {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers
      }
    };

    const response = await fetch(url, authOptions);
    
    if (response.status === 401) {
      // Token expired or invalid
      this.logout();
      throw new Error('Authentication expired. Please login again.');
    }

    return response;
  }

  // Get user profile using JWT
  async getUserProfile() {
    try {
      const response = await this.authenticatedFetch(`${API_BASE_URL}/api/profile_jwt.php`);
      const data = await response.json();
      
      if (data.success) {
        return data.user;
      } else {
        throw new Error(data.error || 'Failed to get user profile');
      }
    } catch (error) {
      throw error;
    }
  }

  // Update user profile using JWT
  async updateProfile(profileData) {
    try {
      const response = await this.authenticatedFetch(`${API_BASE_URL}/api/update_profile_jwt.php`, {
        method: 'POST',
        body: JSON.stringify(profileData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update local user data
        this.user = { ...this.user, ...profileData };
        return data;
      } else {
        throw new Error(data.error || 'Failed to update profile');
      }
    } catch (error) {
      throw error;
    }
  }
}

// Create singleton instance
const authService = new AuthService();
export default authService;
